import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { RootState, useAppSelector } from "src/store";
import { useContactsApi } from "../api";
import {
  resetError,
  resetListNotInGroup as resetList,
  setError,
  setFilterNotInGroup as setFilter,
  setListNotInGroup as setList,
  setLoading,
} from "../slices";

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    phoneNumber: "phone_number",
    validStatus: "valid_status",
    optInDate: "opt_in_date",
    optInType: "opt_in_type",
    optOutDate: "opt_out_date",
    optOutType: "opt_out_type",
    createdBy: "created_by",
    createdAt: "date_created",
    updatedAt: "date_updated",
    dateAdded: "date_added",
  };

  const [field, order] = sort.split("_");
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const useContactsNotInGroupService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filterNotInGroup: filter, listNotInGroup: list, error } = useAppSelector((state) => state.contacts);

  const api = useContactsApi();

  const getContacts = useCallback(async (companyId: string, groupId: string) => {
    const filter = getState().contacts.filterNotInGroup;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getListNotInGroup: true }));

      const response = await api.getContacts({
        companyId,
        notInGroups: groupId,

        limit: filter.limit,
        offset: filter.offset,

        query: filter.query || undefined,
        sort: getServerSortField(filter.sort),
        groups: filter.groups || undefined,
        validStatus: filter.validStatus || undefined,
        isSpam: filter.isSpam || undefined,
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getListNotInGroup: false }));
    }
  }, []);

  const changeContactsFilter = useCallback((filter: Partial<RootState["contacts"]["filterNotInGroup"]>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetContacts = useCallback(() => {
    dispatch(resetList());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getContacts,
    changeContactsFilter,
    resetContacts,
  };
};
